import React, { useRef, useEffect, useState, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';

const CustomOTPInput = forwardRef(({ 
  length = 6, 
  value = [], 
  onChange, 
  onComplete,
  className = '',
  disabled = false,
  autoSubmit = false 
}, ref) => {
  const inputRefs = useRef([]);
  const [focusIndex, setFocusIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const isProcessingRef = useRef(false);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    focusFirst: () => {
      setFocusIndex(0);
      // Use multiple timeouts to ensure focus happens
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].blur(); // First blur any current focus
          setTimeout(() => {
            inputRefs.current[0].focus(); // Then focus the first input
          }, 5);
        }
      }, 10);
    },
    reset: () => {
      setFocusIndex(0);
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].blur();
          setTimeout(() => {
            inputRefs.current[0].focus();
          }, 5);
        }
      }, 10);
    }
  }));

  // Memoize mobile detection to prevent unnecessary re-calculations
  const mobileCheck = useMemo(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isMobileDevice = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    const isSmallScreen = window.innerWidth <= 768;
    return isMobileDevice || isSmallScreen;
  }, []);

  useEffect(() => {
    setIsMobile(mobileCheck);
    
    const checkMobile = () => {
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(mobileCheck || isSmallScreen);
    };

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [mobileCheck]);

  useEffect(() => {
    // Initialize refs array
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  useEffect(() => {
    // Auto-focus first input on mount
    if (inputRefs.current[0] && !disabled && !isProcessingRef.current) {
      inputRefs.current[0].focus();
    }
  }, [disabled]);

  useEffect(() => {
    // Reset focus to first input when all values are cleared
    const allEmpty = value.every(val => val === '' || val === undefined || val === null);
    if (allEmpty && value.length > 0) {
      setFocusIndex(0);
      // Force focus to first input when all values are cleared
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 0);
    }
  }, [value]);

  // Optimized change handler with debouncing for mobile
  const handleInputChange = useCallback((index, inputValue) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    // Handle auto-suggestion case where full OTP might be pasted into one field
    const cleanedInput = inputValue.replace(/[^0-9]/g, '');
    
    if (cleanedInput.length > 1) {
      // This is likely an auto-suggestion - distribute across all fields
      const newValue = new Array(length).fill('');
      
      // Fill the array with the pasted digits
      for (let i = 0; i < Math.min(cleanedInput.length, length); i++) {
        newValue[i] = cleanedInput[i];
      }
      
      onChange(newValue);
      
      // Focus on the next empty field or last field
      const filledCount = Math.min(cleanedInput.length, length);
      const nextFocusIndex = filledCount < length ? filledCount : length - 1;
      
      requestAnimationFrame(() => {
        const nextInput = inputRefs.current[nextFocusIndex];
        if (nextInput) {
          nextInput.focus();
          setFocusIndex(nextFocusIndex);
        }
        isProcessingRef.current = false;
      });

      // Check if complete and trigger auto-submit
      if (newValue.every(val => val !== '') && newValue.length === length) {
        if (autoSubmit && onComplete) {
          setTimeout(() => {
            onComplete(newValue);
          }, 50);
        }
      }
    } else {
      // Normal single digit input
      const digit = cleanedInput.slice(-1);
      
      const newValue = [...value];
      newValue[index] = digit;
      
      // Ensure array has correct length
      while (newValue.length < length) {
        newValue.push('');
      }
      
      onChange(newValue);

      // Move to next input if digit entered
      if (digit && index < length - 1) {
        // Use requestAnimationFrame for better performance on mobile
        requestAnimationFrame(() => {
          const nextInput = inputRefs.current[index + 1];
          if (nextInput) {
            nextInput.focus();
            setFocusIndex(index + 1);
          }
          isProcessingRef.current = false;
        });
      } else {
        isProcessingRef.current = false;
      }

      // Check if complete and trigger auto-submit
      if (newValue.every(val => val !== '') && newValue.length === length) {
        if (autoSubmit && onComplete) {
          // Small delay to ensure UI updates
          setTimeout(() => {
            onComplete(newValue);
          }, 50);
        }
      }
    }
  }, [value, onChange, length, autoSubmit, onComplete]);

  const handleKeyDown = useCallback((index, e) => {
    if (isProcessingRef.current) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      isProcessingRef.current = true;
      
      const newValue = [...value];
      
      if (newValue[index]) {
        // Clear current field if it has value
        newValue[index] = '';
        onChange(newValue);
        isProcessingRef.current = false;
      } else if (index > 0) {
        // Move to previous field if current is empty
        requestAnimationFrame(() => {
          const prevInput = inputRefs.current[index - 1];
          if (prevInput) {
            newValue[index - 1] = '';
            onChange(newValue);
            prevInput.focus();
            setFocusIndex(index - 1);
          }
          isProcessingRef.current = false;
        });
      } else {
        isProcessingRef.current = false;
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      requestAnimationFrame(() => {
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
          setFocusIndex(index - 1);
        }
      });
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      requestAnimationFrame(() => {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          setFocusIndex(index + 1);
        }
      });
    }
  }, [value, onChange, length]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    
    if (pastedData.length > 0) {
      // Create a new array filled with empty strings
      const newValue = new Array(length).fill('');
      
      // For mobile, always start from the beginning regardless of current focus
      const startIndex = isMobile ? 0 : focusIndex;
      
      // Fill the array with pasted digits
      for (let i = 0; i < Math.min(pastedData.length, length); i++) {
        newValue[i] = pastedData[i];
      }
      
      onChange(newValue);
      
      // Focus on the next empty field or last field
      const filledCount = Math.min(pastedData.length, length);
      const nextFocusIndex = filledCount < length ? filledCount : length - 1;
      
      requestAnimationFrame(() => {
        const nextInput = inputRefs.current[nextFocusIndex];
        if (nextInput) {
          nextInput.focus();
          setFocusIndex(nextFocusIndex);
        }
        isProcessingRef.current = false;
      });

      // Check if complete and trigger auto-submit
      if (newValue.every(val => val !== '') && newValue.length === length) {
        if (autoSubmit && onComplete) {
          setTimeout(() => {
            onComplete(newValue);
          }, 50);
        }
      }
    } else {
      isProcessingRef.current = false;
    }
  }, [value, onChange, length, focusIndex, autoSubmit, onComplete, isMobile]);

  const handleFocus = useCallback((index) => {
    // Check if all values are empty - if so, always focus first input
    const allEmpty = value.every(val => val === '' || val === undefined || val === null);
    if (allEmpty && value.length > 0) {
      setFocusIndex(0);
      // Force focus to first input if all are empty
      if (index !== 0 && inputRefs.current[0]) {
        setTimeout(() => {
          inputRefs.current[0].focus();
        }, 0);
        return;
      }
    }
    setFocusIndex(index);
  }, [value]);

  const handleClick = useCallback((index) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      setFocusIndex(index);
      // Move cursor to end on mobile
      if (isMobile) {
        requestAnimationFrame(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        });
      }
    }
  }, [isMobile]);

  // Memoize input elements to prevent unnecessary re-renders
  const inputElements = useMemo(() => {
    return Array.from({ length }, (_, index) => (
      <input
        key={index}
        ref={(el) => (inputRefs.current[index] = el)}
        type={isMobile ? "tel" : "text"}
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength="1"
        value={value[index] || ''}
        onChange={(e) => handleInputChange(index, e.target.value)}
        onKeyDown={(e) => handleKeyDown(index, e)}
        onPaste={handlePaste}
        onFocus={() => handleFocus(index)}
        onClick={() => handleClick(index)}
        disabled={disabled}
        className="custom-otp-input"
        autoComplete="one-time-code"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    ));
  }, [length, isMobile, value, disabled, handleInputChange, handleKeyDown, handlePaste, handleFocus, handleClick]);

  return (
    <div className={`custom-otp-container ${className}`}>
      {inputElements}
    </div>
  );
});

export default React.memo(CustomOTPInput);
